import { paidMediaDigits, paidMediaNumericToken } from "./PaidMediaNumberFormat";

/**
 * Derive a currency label (e.g. "PKR", "$", "€") from a backend-formatted money string.
 * When the API omits a prefix, returns null so callers format numbers without a forced currency.
 */
export function extractCurrencyPrefixFromFormattedValue(
  formatted: string | null | undefined
): string | null {
  if (!formatted || typeof formatted !== "string") {
    return null;
  }

  // Eastern Arabic-Indic digits are matched as well as ASCII ones: under `lang=ar` the API
  // returns amounts like "PKR ١,٤٣٠,٠٤٣", and an ASCII-only `\d` would find no digit at all and
  // drop the currency code from every Arabic money cell.
  const trimmed = formatted.trim();
  const match = trimmed.match(/^([^\d٠-٩۰-۹]+?)\s*[\d٠-٩۰-۹]/);
  if (!match?.[1]) {
    return null;
  }

  const prefix = match[1].trim();
  return prefix || null;
}

export function extractCurrencyPrefixFromKpiItems(
  items: Array<{ id?: string; title?: string; formatted_value?: string }>
): string | null {
  const norm = (s: string | undefined) => s?.trim().toLowerCase() ?? "";

  const preferredKeys = [
    "total-spend",
    "total spend",
    "total_cost",
    "total cost",
    "cpc",
    "cpa",
    "cost per click",
    "cost per acquisition",
  ];

  for (const key of preferredKeys) {
    const hit = items.find(
      item => norm(item.id) === key || norm(item.title) === key
    );
    const prefix = extractCurrencyPrefixFromFormattedValue(hit?.formatted_value);
    if (prefix) {
      return prefix;
    }
  }

  for (const item of items) {
    const prefix = extractCurrencyPrefixFromFormattedValue(item?.formatted_value);
    if (prefix) {
      return prefix;
    }
  }

  return null;
}

export function formatPaidMediaMoney(
  amount: number,
  currencyPrefix: string | null,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  const min = options?.minimumFractionDigits ?? 2;
  const max = options?.maximumFractionDigits ?? 2;
  const num = paidMediaDigits(amount, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
  const prefix = currencyPrefix?.trim();
  // The currency code and its digits are wrapped together, so the code can't be reordered to
  // the far side of the amount in an RTL paragraph.
  return paidMediaNumericToken(prefix ? `${prefix} ${num}` : num);
}

/** Slightly tighter decimals for CPC/CPA-style cells (matches prior Meta Ads behavior). */
export function formatPaidMediaMoneyShort(amount: number, currencyPrefix: string | null): string {
  const min = amount < 10 ? 2 : 0;
  const max = amount < 10 ? 2 : 0;
  return formatPaidMediaMoney(amount, currencyPrefix, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
}
